// @givmo/app-charity — profile edit form (CPA-WEB6).
//
// Edit mission / website / contact email / contact phone via `charityAdminUpdateCharityProfile` (PATCH).
// Owner-only: the parent renders the editable form only for an Owner; a Member gets the read-only view.
//
// PATCH is MINIMAL (`buildProfilePatch`): only changed fields are sent, an emptied field clears (null), an
// unchanged one is omitted. On save we invalidate the profile query (parent supplies the callback) and
// toast honestly — the server's own sentence, verbatim, for any 4xx.
//
// The Website input WRITES `organization_domain` (CPA-WEB74): it is one field, the charity's website and
// its verification anchor at once. <DomainGuard> below shows what the typed URL resolves to and says so.
// The input keeps the label "Website" because that is the thing the Owner is editing; the domain is the
// consequence, which is exactly what the card explains.

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { charityAdminUpdateCharityProfileMutation } from '@givmo/api-client/query';
import { isGivmoError, type CharityProfileOut } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Icon,
  TextField,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { PencilLine } from 'lucide-react';
import {
  EMAIL_MESSAGE,
  MISSION_MAX,
  MISSION_MAX_MESSAGE,
  CONTACT_PHONE_MAX,
  CONTACT_PHONE_MAX_MESSAGE,
  WEBSITE_MESSAGE,
  buildProfilePatch,
  isEmailish,
  isWebsiteish,
  profileSchema,
  profileToForm,
  type EditableField,
  type ProfileFormValues,
} from './profile-schemas';
import { HumanEditedHint } from './human-edited-hint';
import { useUnsavedChanges } from '../../lib/unsaved-changes';

const PREFIX = 'profile-form';

export interface ProfileFormProps {
  charityId: string;
  /** The loaded profile — seeds the form + the dirty-diff baseline. */
  profile: CharityProfileOut;
  /**
   * Which fields were charity-entered (human) — drives the subtle "you edited this" hints. Only a
   * field the provenance ledger tracks can be in it (`PROVENANCE_FIELD`), so only those render a
   * hint: Contact email / Contact phone carry none (CPA-WEB75).
   */
  humanFields: ReadonlySet<EditableField>;
  /** Invalidate/refresh the profile query after a successful save. */
  onSaved: () => void | Promise<unknown>;
}

export function ProfileForm({ charityId, profile, humanFields, onSaved }: ProfileFormProps) {
  const [form, setForm] = useState<ProfileFormValues>(() => profileToForm(profile));
  const [touched, setTouched] = useState<Record<keyof ProfileFormValues, boolean>>({
    mission: false,
    website: false,
    contact_email: false,
    contact_phone: false,
  });

  const save = useMutation({
    ...charityAdminUpdateCharityProfileMutation(),
    onSuccess: async () => {
      await onSaved();
      toast.success('Profile saved.');
    },
    onError: (error) => {
      toast.error(
        isGivmoError(error) ? error.message : 'We couldn’t save your changes. Please try again.',
      );
    },
  });

  const parsed = profileSchema.safeParse(form);
  const isValid = parsed.success;

  // The minimal PATCH for the current form vs. the loaded profile; `null` when nothing changed.
  const patch = useMemo(() => buildProfilePatch(profile, form), [profile, form]);
  const isDirty = patch !== null;
  useUnsavedChanges(isDirty && !save.isPending);

  const websiteError = touched.website && !isWebsiteish(form.website) ? WEBSITE_MESSAGE : undefined;
  const emailError =
    touched.contact_email && !isEmailish(form.contact_email) ? EMAIL_MESSAGE : undefined;
  const missionError =
    touched.mission && form.mission.length > MISSION_MAX ? MISSION_MAX_MESSAGE : undefined;
  const phoneError =
    touched.contact_phone && form.contact_phone.length > CONTACT_PHONE_MAX
      ? CONTACT_PHONE_MAX_MESSAGE
      : undefined;

  function update<K extends keyof ProfileFormValues>(key: K, value: ProfileFormValues[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!isValid || !isDirty || save.isPending || patch === null) return;
    save.mutate({ path: { charity_id: charityId }, body: patch });
  }

  return (
    <Card testId={`${PREFIX}-card`}>
      <CardHeader
        title="Organization details"
        subtitle="Your mission, website, and how supporters can reach you."
      />
      <CardBody>
        <form
          className="flex flex-col gap-16"
          noValidate
          onSubmit={onSubmit}
          {...testIdProp(`${PREFIX}-form`)}
        >
          <div className="flex flex-col gap-2">
            <Textarea
              label="Mission"
              rows={4}
              placeholder="Describe your organization’s mission in a sentence or two."
              value={form.mission}
              onChange={(e) => update('mission', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, mission: true }))}
              error={!!missionError}
              errorMessage={missionError}
              testId={`${PREFIX}-mission`}
            />
            <span
              className="text-right text-s12 tabular-nums text-text-tertiary"
              {...testIdProp(`${PREFIX}-mission-count`)}
            >
              {form.mission.length}/{MISSION_MAX}
            </span>
            <HumanEditedHint show={humanFields.has('mission')} />
          </div>

          <div className="flex flex-col gap-2">
            <TextField
              label="Website"
              type="url"
              inputMode="url"
              autoComplete="off"
              placeholder="https://your-organization.org"
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, website: true }))}
              error={!!websiteError}
              errorMessage={websiteError}
              testId={`${PREFIX}-website`}
            />
            {/* Keyed by the FORM field, as every hint here is. The panel translates it to the
                LEDGER field (`organization_domain`) when it builds the set — one translation, in
                one place, rather than a second vocabulary leaking into the form. */}
            <HumanEditedHint show={humanFields.has('website')} />
          </div>

          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <TextField
                label="Contact email"
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder="hello@your-organization.org"
                value={form.contact_email}
                onChange={(e) => update('contact_email', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, contact_email: true }))}
                error={!!emailError}
                errorMessage={emailError}
                testId={`${PREFIX}-contact-email`}
              />
            </div>
            <div className="flex flex-col gap-2">
              <TextField
                label="Contact phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder="(555) 123-4567"
                value={form.contact_phone}
                onChange={(e) => update('contact_phone', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, contact_phone: true }))}
                error={!!phoneError}
                errorMessage={phoneError}
                testId={`${PREFIX}-contact-phone`}
              />
              <span
                className="text-right text-s12 tabular-nums text-text-tertiary"
                {...testIdProp(`${PREFIX}-contact-phone-count`)}
              >
                {form.contact_phone.length}/{CONTACT_PHONE_MAX}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-8">
            <Button
              type="submit"
              appearance="saas"
              tone="primary"
              leadingIcon={<Icon icon={PencilLine} size={16} aria-hidden />}
              disabled={!isValid || !isDirty}
              loading={save.isPending}
              testId={`${PREFIX}-save`}
            >
              Save changes
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
